import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["preview", "form", "webPreview", "pdfPreview", "webButton", "pdfButton"]
  static values = { 
    profileId: String,
    previewUrl: String,
    previewMethod: String
  }

  connect() {
    // Auto-refresh preview every 2 seconds if form has changes
    this.startAutoRefresh()
    // Set initial state
    this.currentPreview = 'web'
    this.updateButtonStates()
  }

  disconnect() {
    this.stopAutoRefresh()
  }

  startAutoRefresh() {
    this.refreshInterval = setInterval(() => {
      if (this.hasFormTarget && this.formHasChanges()) {
        this.refreshPreview()
      }
    }, 2000)
  }

  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  }

  formHasChanges() {
    // Check if any form field has been modified
    if (!this.hasFormTarget) {
      return false
    }
    
    const form = this.formTarget
    if (!form || !(form instanceof HTMLFormElement)) {
      return false
    }
    
    const formData = new FormData(form)
    const currentData = Array.from(formData.entries()).map(([key, value]) => `${key}=${value}`).join('&')
    
    if (!this.lastFormData) {
      this.lastFormData = currentData
      return false
    }
    
    const hasChanges = this.lastFormData !== currentData
    this.lastFormData = currentData
    return hasChanges
  }

  async refreshPreview() {
    if (!this.previewUrlValue) {
      // Show message if no preview URL available
      if (this.hasWebPreviewTarget) {
        this.webPreviewTarget.innerHTML = `
          <div class="text-center py-12">
            <i class="fas fa-info-circle text-4xl text-blue-500 mb-4"></i>
            <h4 class="text-lg font-medium text-gray-900 mb-2">Vorschau nicht verfügbar</h4>
            <p class="text-gray-500">Keine Vorschau-URL konfiguriert.</p>
          </div>
        `
      }
      return
    }

    if (!this.hasFormTarget) {
      return
    }

    try {
      const form = this.formTarget
      if (!form || !(form instanceof HTMLFormElement)) {
        return
      }
      
      const formData = new FormData(form)
      
      // Add a flag to indicate this is a preview request
      formData.append('preview', 'true')
      
      const method = this.previewMethodValue || 'POST'
      const response = await fetch(this.previewUrlValue, {
        method: method,
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const html = await response.text()
        if (this.hasWebPreviewTarget) {
          this.webPreviewTarget.innerHTML = html
        }
      } else {
        // Show error message
        if (this.hasWebPreviewTarget) {
          this.webPreviewTarget.innerHTML = `
            <div class="text-center py-12">
              <i class="fas fa-exclamation-triangle text-4xl text-yellow-500 mb-4"></i>
              <h4 class="text-lg font-medium text-gray-900 mb-2">Vorschau-Fehler</h4>
              <p class="text-gray-500">Die Vorschau konnte nicht geladen werden.</p>
            </div>
          `
        }
      }
    } catch (error) {
      if (this.hasWebPreviewTarget) {
        this.webPreviewTarget.innerHTML = `
          <div class="text-center py-12">
            <i class="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
            <h4 class="text-lg font-medium text-gray-900 mb-2">Verbindungsfehler</h4>
            <p class="text-gray-500">Verbindung zur Vorschau konnte nicht hergestellt werden.</p>
          </div>
        `
      }
    }
  }

  togglePreview() {
    if (this.previewTarget.classList.contains('hidden')) {
      this.showPreview()
    } else {
      this.hidePreview()
    }
  }

  showPreview() {
    this.previewTarget.classList.remove('hidden')
    this.refreshPreview()
  }

  hidePreview() {
    this.previewTarget.classList.add('hidden')
  }

  showWebPreview() {
    this.currentPreview = 'web'
    this.updateButtonStates()
    this.showPreviewPanel('web')
  }

  showPdfPreview() {
    this.currentPreview = 'pdf'
    this.updateButtonStates()
    this.showPreviewPanel('pdf')
  }

  showPreviewPanel(type) {
    if (type === 'web') {
      this.webPreviewTarget.classList.remove('hidden')
      this.pdfPreviewTarget.classList.add('hidden')
    } else {
      this.webPreviewTarget.classList.add('hidden')
      this.pdfPreviewTarget.classList.remove('hidden')
    }
  }

  updateButtonStates() {
    if (this.currentPreview === 'web') {
      this.webButtonTarget.className = 'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 bg-white text-blue-600 shadow-sm'
      this.pdfButtonTarget.className = 'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 text-gray-500 hover:text-gray-700'
    } else {
      this.webButtonTarget.className = 'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 text-gray-500 hover:text-gray-700'
      this.pdfButtonTarget.className = 'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 bg-white text-red-600 shadow-sm'
    }
  }
}
